import type { RootStore } from 'logic';
import { action, computed, makeObservable, observable } from 'mobx';

export class UiSourceRowSelectorStore {
  selectedRowIds = observable.set<string>();
  lastSelectedRowId = '';

  constructor(_rootStore: RootStore) {
    makeObservable<this>(this, {
      selectedRowIds: observable,
      lastSelectedRowId: observable,
      toggleRow: action.bound,
      selectRow: action.bound,
      selectRange: action.bound,
      deselectAll: action.bound,
      selectedRowIdsList: computed,
      selectedCount: computed,
      hasSelection: computed,
    });
  }

  toggleRow(rowId: string) {
    if (this.selectedRowIds.has(rowId)) {
      this.selectedRowIds.delete(rowId);
    } else {
      this.selectedRowIds.add(rowId);
    }
    this.lastSelectedRowId = rowId;
  }

  selectRow(rowId: string) {
    this.selectedRowIds.clear();
    this.selectedRowIds.add(rowId);
    this.lastSelectedRowId = rowId;
  }

  selectRange(allRowIds: string[], targetRowId: string) {
    if (!this.lastSelectedRowId) {
      this.selectedRowIds.clear();
      this.selectedRowIds.add(targetRowId);
      this.lastSelectedRowId = targetRowId;
      return;
    }

    const lastIndex = allRowIds.indexOf(this.lastSelectedRowId);
    const targetIndex = allRowIds.indexOf(targetRowId);

    if (lastIndex === -1 || targetIndex === -1) {
      this.selectedRowIds.clear();
      this.selectedRowIds.add(targetRowId);
      this.lastSelectedRowId = targetRowId;
      return;
    }

    // Finder-style: replace entire selection with the range
    this.selectedRowIds.clear();
    const start = Math.min(lastIndex, targetIndex);
    const end = Math.max(lastIndex, targetIndex);

    for (let i = start; i <= end; i++) {
      this.selectedRowIds.add(allRowIds[i]);
    }
    // Keep anchor at the original click point, not the shift-click target
  }

  deselectAll() {
    this.selectedRowIds.clear();
  }

  isRowSelected(rowId: string): boolean {
    return this.selectedRowIds.has(rowId);
  }

  get selectedRowIdsList(): string[] {
    return Array.from(this.selectedRowIds);
  }

  get selectedCount(): number {
    return this.selectedRowIds.size;
  }

  get hasSelection(): boolean {
    return this.selectedRowIds.size > 0;
  }
}
